import { useRef, useState, type ReactNode } from 'react';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { usePhoneWidth } from '@/hooks';
import { cn } from '@/lib/utils';

const tokenClassName =
  'inline-flex items-baseline rounded-md border border-primary/30 text-left bg-primary/10 px-2 font-semibold leading-snug text-primary transition-colors hover:border-primary data-[state=open]:border-primary';

/** Which blank of the sentence is open: one at a time, across every stage's sentence. */
export interface BlankFocus {
  open: string | null;
  setOpen: (id: string | null) => void;
  /** The open blank's sheet as its token last drew it, so the sheet outlasts a token that remounts or leaves the sentence. */
  sheet: { current: BlankSheetContent | null };
}

interface BlankSheetContent {
  id: string;
  label: string;
  panel: (close: () => void) => ReactNode;
}

export function useBlankFocus(): BlankFocus {
  const [open, setOpen] = useState<string | null>(null);
  const sheet = useRef<BlankSheetContent | null>(null);
  return { open, setOpen, sheet };
}

/**
 * The phone sheet of the open blank. The sentence changes shape as its blanks fill, which remounts them, so the sheet lives
 * here instead: render it once, after every blank, where it stays mounted whatever the sentence becomes. `open` clears only
 * once the sheet has slid away, so a blank that remounts meanwhile doesn't open again.
 */
export function BlankSheet({ focus }: { focus: BlankFocus }) {
  const phone = usePhoneWidth();
  const content = focus.sheet.current;
  if (!phone || !content || content.id !== focus.open) return null;
  return (
    <BottomSheet key={content.id} label={content.label} onClose={() => focus.setOpen(null)}>
      {(close) => <div className="pb-2">{content.panel(close)}</div>}
    </BottomSheet>
  );
}

/**
 * One blank in the sentence: a token that opens what fills it, in a popover beside it on a wide screen and in the
 * `BlankSheet` at phone width. `children` gets the close that suits each: the sheet slides away before it unmounts.
 */
export function Blank(props: {
  id: string;
  label: ReactNode;
  /** Names the token and the sheet. */
  aria: string;
  testId?: string;
  /** Not filled yet: drawn dashed, so it reads as a question. */
  empty?: boolean;
  /** For a panel with a search and a list in it. */
  wide?: boolean;
  focus: BlankFocus;
  children: (close: () => void) => ReactNode;
}) {
  const phone = usePhoneWidth();
  const { focus } = props;
  const isOpen = focus.open === props.id;
  const close = () => focus.setOpen(null);
  const token = (
    <button
      type="button"
      aria-label={props.aria}
      data-testid={props.testId}
      className={cn(tokenClassName, props.empty && 'border-dashed font-medium text-primary/80')}
      {...(phone ? { 'aria-haspopup': 'dialog' as const, 'aria-expanded': isOpen, onClick: () => focus.setOpen(props.id) } : {})}
    >
      {props.label}
    </button>
  );

  if (phone) {
    if (isOpen) focus.sheet.current = { id: props.id, label: props.aria, panel: props.children };
    return token;
  }
  return (
    <Popover open={isOpen} onOpenChange={(open) => focus.setOpen(open ? props.id : null)}>
      <PopoverTrigger asChild>{token}</PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn('p-4 motion-reduce:animate-none', props.wide ? 'w-[min(440px,calc(100vw-24px))]' : 'w-[min(380px,calc(100vw-24px))]')}
        // Focus going back to this token would close the blank opened after it, as a click outside.
        onCloseAutoFocus={(e) => focus.open !== null && e.preventDefault()}
      >
        {props.children(close)}
      </PopoverContent>
    </Popover>
  );
}
